import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, Mail, Phone, CheckCircle, Play, ArrowRight, Layers, Car, Users, Building, Utensils, Home } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import { SiteDataProvider, useSiteData } from '../context/SiteDataContext';

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const NYZA_LOGO_URL = "/images/nyza-logo.webp";

const iconMap = { 'car': Car, 'users': Users, 'layers': Layers, 'building': Building, 'utensils': Utensils, 'home': Home };

// Navigation
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 py-4 md:py-6 bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="relative z-10">
            <img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-8 md:h-10" style={{ filter: 'brightness(0)' }} />
          </Link>
          <div className="hidden md:flex items-center bg-white/90 backdrop-blur-md rounded-full px-2 py-1.5 shadow-md border border-gray-200">
            <Link to="/" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide rounded-full text-gray-600 hover:text-gray-900">HOME</Link>
            <Link to="/references" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide rounded-full text-gray-600 hover:text-gray-900">REFERENCES</Link>
            <Link to="/products" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide rounded-full text-gray-900 bg-white shadow-sm">PRODUCTS</Link>
            <Link to="/marketing" className="px-4 py-1.5 text-xs font-medium uppercase tracking-wide rounded-full text-gray-600 hover:text-gray-900">MARKETING</Link>
          </div>
          <Link to="/#contact" className="hidden md:block bg-[#9A45FF] text-white text-xs font-medium uppercase tracking-wide px-5 py-2 rounded-full hover:bg-[#8035E8] transition-colors">CONTACT</Link>
          <button className="md:hidden p-2 text-gray-900" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {isOpen && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="md:hidden mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 p-4">
            <Link to="/" className="block py-2 text-sm text-gray-900 font-medium uppercase" onClick={() => setIsOpen(false)}>HOME</Link>
            <Link to="/references" className="block py-2 text-sm text-gray-900 font-medium uppercase" onClick={() => setIsOpen(false)}>REFERENCES</Link>
            <Link to="/products" className="block py-2 text-sm text-purple-600 font-medium uppercase" onClick={() => setIsOpen(false)}>PRODUCTS</Link>
            <Link to="/marketing" className="block py-2 text-sm text-gray-900 font-medium uppercase" onClick={() => setIsOpen(false)}>MARKETING</Link>
            <Link to="/#contact" className="block w-full text-center mt-3 bg-[#9A45FF] text-white text-sm font-medium uppercase px-4 py-2 rounded-full" onClick={() => setIsOpen(false)}>CONTACT</Link>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', surname: '', email: '', message: '', privacy_accepted: false });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.privacy_accepted) { toast.error('Please accept the privacy policy'); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { name: `${formData.name} ${formData.surname}`, email: formData.email, service: 'Product Inquiry', message: formData.message, privacy_accepted: formData.privacy_accepted });
      toast.success("Message sent!");
      setFormData({ name: '', surname: '', email: '', message: '', privacy_accepted: false });
    } catch (err) { toast.error('Error sending message.'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FFFFFF 0%, #F5F0FF 30%, #E8D4FF 70%, #D4B8FF 100%)' }}>
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="hidden lg:block"><img src="/images/contact.jpg" alt="Contact" className="rounded-2xl shadow-2xl w-full object-cover" /></div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">SHOOT US<br/><span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">A MESSAGE</span></h2>
            <div className="flex flex-wrap gap-4 mb-6">
              <a href="mailto:workwith@nyza-studio.at" className="flex items-center gap-2 text-sm text-purple-600"><Mail className="w-4 h-4" />workwith@nyza-studio.at</a>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <input type="text" placeholder="First Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
                <input type="text" placeholder="Last Name *" value={formData.surname} onChange={(e) => setFormData({...formData, surname: e.target.value})} required className="px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
              </div>
              <input type="email" placeholder="Email *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none bg-white/80" />
              <textarea placeholder="Your Message *" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-purple-500 outline-none resize-none bg-white/80" />
              <label className="flex items-start gap-3"><input type="checkbox" checked={formData.privacy_accepted} onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})} className="mt-1 w-4 h-4 rounded text-purple-600" /><span className="text-xs text-gray-500">I accept the <Link to="/datenschutz" className="text-purple-600">Privacy Policy</Link> *</span></label>
              <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white py-4 rounded-xl font-semibold disabled:opacity-50">{isSubmitting ? 'Sending...' : 'Send Message'}</button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-16 px-4" style={{ background: 'linear-gradient(180deg, #D4B8FF 0%, #9A45FF 100%)' }}>
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
        <div className="md:col-span-2">
          <img src={NYZA_LOGO_URL} alt="NYZA Studio" className="h-10 mb-4" style={{ filter: 'brightness(0) invert(1)' }} />
          <p className="text-white/80 text-sm max-w-md">Engineering digital excellence through curated software solutions.</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Solutions</h4>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/marketing" className="hover:text-white">Marketing</Link></li>
            <li><Link to="/products" className="hover:text-white">Products</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4 uppercase tracking-wide text-sm">Legal</h4>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/impressum" className="hover:text-white">Imprint</Link></li>
            <li><Link to="/datenschutz" className="hover:text-white">Privacy</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/20 pt-8 text-center">
        <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} NYZA Studio. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Product Detail Content
const ProductDetailContent = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Default product data
  const defaultProducts = {
    'cardealeros': {
      name: 'CardealerOS',
      tagline: 'Automotive Control.',
      description: 'The definitive operating system for modern car dealerships. Streamline inventory, sales, and customer relations with a high-performance custom web solution.',
      icon: 'car',
      image: 'https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=1200&h=800&fit=crop&q=80',
      features: [
        { title: 'Advanced Inventory Intelligence', description: 'Gain deep insights into your stock turnover, aging units, and regional demand forecasts.', icon: 'layers' },
        { title: 'Mobile CRM', description: 'Manage leads and close deals from the showroom floor with our native-feel mobile companion app.', icon: 'users' },
        { title: 'Contract Automation', description: 'Generate digital paperwork, e-signatures, and compliance docs in seconds, not hours.', icon: 'layers' },
        { title: 'Multi-Channel Sync', description: 'Push inventory to Autotrader, Facebook, and your site with one click.', icon: 'layers' },
      ],
      benefits: ['Unlimited Inventory Units', '24/7 Dedicated Support', 'Custom Branding'],
      price: '99',
    },
    'clubos': {
      name: 'ClubOS',
      tagline: 'Nightlife Redefined.',
      description: 'Exclusive member management for nightlife and private venues with tiered access control and real-time analytics.',
      icon: 'users',
      image: 'https://images.unsplash.com/photo-1566737236500-c8ac43014a67?w=1200&h=800&fit=crop&q=80',
      features: [
        { title: 'Member Tiers', description: 'Create VIP levels with exclusive perks and automated upgrades.', icon: 'users' },
        { title: 'Event Management', description: 'Plan and manage events with guest lists and capacity tracking.', icon: 'layers' },
        { title: 'Real-time Analytics', description: 'Track attendance, revenue, and member engagement live.', icon: 'layers' },
      ],
      benefits: ['Unlimited Members', 'White-label Solution', 'API Access'],
      price: '149',
    },
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/products/${slug}`);
        setProduct(response.data);
      } catch (error) {
        // Use default data
        setProduct(defaultProducts[slug] || defaultProducts['cardealeros']);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9f9] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#f9f9f9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product not found</h1>
          <Link to="/products" className="text-purple-600 hover:underline">Back to Products</Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[product.icon] || Layers;

  return (
    <div className="min-h-screen bg-[#f9f9f9]">
      <Navigation />
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="relative px-6 pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="z-10">
            <span className="text-purple-600 font-bold tracking-widest uppercase text-sm mb-4 block">Product Spotlight</span>
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-[0.95] mb-6">
              {product.name}<br/>
              <span className="bg-gradient-to-r from-purple-600 to-purple-400 bg-clip-text text-transparent">{product.tagline}</span>
            </h1>
            <p className="text-gray-600 text-xl mb-10 max-w-lg leading-relaxed">{product.description}</p>
            <div className="flex flex-wrap gap-4">
              <Link to="/schedule-demo" className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:scale-105 transition-transform flex items-center gap-2">
                Start Your Trial <ArrowRight className="w-5 h-5" />
              </Link>
              <button className="border border-gray-300 text-purple-600 px-8 py-4 rounded-full font-semibold hover:bg-gray-50 transition-colors">
                View Demo
              </button>
            </div>
          </div>
          
          {/* Mockup */}
          <div className="relative group">
            <div className="absolute inset-0 bg-purple-500/10 blur-[120px] rounded-full -z-10"></div>
            <div className="relative bg-gray-900 p-2 rounded-[2rem] shadow-2xl overflow-hidden border-4 border-gray-800">
              <div className="bg-gray-800 h-6 w-full flex items-center px-4 space-x-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-500/50"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-amber-500/50"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/50"></div>
              </div>
              {product.image ? (
                <img alt={product.name} src={product.image} className="w-full h-auto rounded-b-xl" />
              ) : (
                <div className="w-full aspect-video bg-gradient-to-br from-purple-100 to-purple-200 rounded-b-xl flex items-center justify-center">
                  <IconComponent className="w-24 h-24 text-purple-400" />
                </div>
              )}
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-2xl border border-gray-100 max-w-[200px]">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="font-bold text-gray-900">Live Sync</span>
              </div>
              <p className="text-xs text-gray-600">Real-time updates across all platforms.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="bg-gray-50 py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl font-bold tracking-tight mb-4 text-gray-900">Precision Engineering</h2>
            <p className="text-gray-600 max-w-xl">Every feature is designed to eliminate friction and maximize efficiency.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.features?.map((feature, index) => (
              <div key={index} className={`bg-white p-10 rounded-2xl shadow-sm border border-gray-100 hover:border-purple-200 transition-colors ${index === 0 ? 'md:col-span-2' : ''}`}>
                <Layers className="w-10 h-10 text-purple-600 mb-6" />
                <h3 className="text-xl font-bold mb-4 text-gray-900">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="bg-[#9A45FF] text-white py-24 px-6 mx-6 rounded-[3rem]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-8 leading-tight">Investment for Growth.</h2>
            <ul className="space-y-6">
              {product.benefits?.map((benefit, index) => (
                <li key={index} className="flex items-start gap-4">
                  <CheckCircle className="w-6 h-6 mt-1" />
                  <div>
                    <span className="block font-bold text-xl">{benefit}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-12 rounded-[2.5rem] shadow-2xl text-gray-900">
            <span className="text-purple-600 font-bold uppercase tracking-widest text-xs mb-4 block">Standard Plan</span>
            <div className="mb-8">
              <span className="text-5xl font-bold">{product.price || '99'}€</span>
              <span className="text-gray-500">/month</span>
            </div>
            <p className="text-gray-600 mb-10 text-sm leading-relaxed">Perfect for businesses looking to modernize their workflow. Includes all core features.</p>
            <Link to="/schedule-demo" className="block w-full bg-purple-600 text-white py-5 rounded-full font-bold text-center hover:bg-purple-700 transition-colors mb-6">
              Start 14-Day Free Trial
            </Link>
            <p className="text-center text-xs text-gray-500">No credit card required. Cancel anytime.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold tracking-tight mb-6 text-gray-900">Ready to outpace the competition?</h2>
          <p className="text-gray-600 text-lg mb-12">Join the industry leaders using {product.name} to dominate their markets.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/schedule-demo" className="bg-purple-600 text-white px-12 py-5 rounded-full font-bold shadow-xl hover:scale-105 transition-transform">
              Get Started Now
            </Link>
            <Link to="/products" className="bg-gray-100 text-gray-900 px-12 py-5 rounded-full font-bold hover:bg-gray-200 transition-colors">
              View All Products
            </Link>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default function ProductDetail() {
  return (
    <SiteDataProvider>
      <ProductDetailContent />
    </SiteDataProvider>
  );
}
